import { useCallback, useEffect, useState } from 'react';
import { FlatList, Pressable, Text, View } from 'react-native';
import { useFocusEffect, useRouter } from 'expo-router';
import { useAuth } from '../context/AuthContext';
import { listUsers, getUserStats, AdminUserListItem, AdminUserStats } from '../api/admin';
import { Button, Card, CoinIcon, EmptyState, ErrorText, FormInput, ScreenContainer } from '../components/ui';
import { colors, fonts } from '../theme';
import { ApiError } from '../api/client';

function StatTile({ label, value }: { label: string; value: number }) {
  return (
    <View
      style={{
        flex: 1,
        minWidth: '47%',
        backgroundColor: colors.card,
        borderRadius: 14,
        borderWidth: 1,
        borderColor: colors.border,
        padding: 12,
      }}
    >
      <Text style={{ fontSize: 20, fontFamily: fonts.extraBold, color: colors.text }}>{value}</Text>
      <Text style={{ color: colors.textMuted, fontSize: 12, marginTop: 2 }}>{label}</Text>
    </View>
  );
}

const ROLE_TABS: { label: string; value?: string }[] = [
  { label: 'Tất cả', value: undefined },
  { label: 'Người nhận việc', value: 'REVIEWER' },
  { label: 'Người đăng việc', value: 'SHOP_OWNER' },
  { label: 'Admin', value: 'ADMIN' },
];

export default function AdminUsersScreen() {
  const { token } = useAuth();
  const router = useRouter();
  const [search, setSearch] = useState('');
  const [role, setRole] = useState<string | undefined>(undefined);
  const [page, setPage] = useState(1);
  const [users, setUsers] = useState<AdminUserListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<AdminUserStats | null>(null);

  useEffect(() => {
    if (!token) return;
    getUserStats(token)
      .then(setStats)
      .catch(() => {
        // Non-critical header widget — the list below still works without it.
      });
  }, [token]);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const result = await listUsers(token, { search: search || undefined, role, page });
      setUsers(result.users);
      setTotal(result.total);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Tải danh sách thất bại');
    } finally {
      setLoading(false);
    }
  }, [token, search, role, page]);

  useFocusEffect(
    useCallback(() => {
      load();
    }, [load]),
  );

  const pageSize = 20;
  const hasNextPage = page * pageSize < total;

  return (
    <ScreenContainer>
      {stats ? (
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
          <StatTile label="Người dùng thực" value={stats.totalRealUsers} />
          <StatTile label="Đăng ký làm nhiệm vụ" value={stats.reviewerCount} />
          <StatTile label="Đăng ký doanh nghiệp" value={stats.businessOwnerCount} />
          <StatTile label="Đăng ký cá nhân giao việc" value={stats.individualPosterCount} />
        </View>
      ) : null}
      <FormInput
        value={search}
        onChangeText={(v) => {
          setSearch(v);
          setPage(1);
        }}
        placeholder="Tìm theo tên hoặc email"
      />
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
        {ROLE_TABS.map((tab) => (
          <Pressable
            key={tab.label}
            onPress={() => {
              setRole(tab.value);
              setPage(1);
            }}
            style={{
              paddingVertical: 6,
              paddingHorizontal: 12,
              borderRadius: 999,
              backgroundColor: role === tab.value ? colors.primary : colors.primarySoft,
            }}
          >
            <Text
              style={{
                color: role === tab.value ? '#fff' : colors.primary,
                fontFamily: fonts.semiBold,
                fontSize: 13,
              }}
            >
              {tab.label}
            </Text>
          </Pressable>
        ))}
      </View>
      <ErrorText>{error}</ErrorText>
      <Text style={{ color: colors.textMuted, fontSize: 12, marginBottom: 6 }}>
        {total} người dùng
      </Text>
      <FlatList
        data={users}
        keyExtractor={(item) => item.id}
        refreshing={loading}
        onRefresh={load}
        ListEmptyComponent={!loading ? <EmptyState text="Không tìm thấy người dùng nào" /> : null}
        renderItem={({ item }) => (
          <Pressable onPress={() => router.push(`/admin-user-detail/${item.id}`)}>
            <Card>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <View style={{ flex: 1 }}>
                  <Text style={{ fontFamily: fonts.bold, color: colors.text }}>
                    {item.name} {item.suspendedAt ? '🚫' : ''}
                  </Text>
                  <Text style={{ color: colors.textMuted, fontSize: 12 }}>{item.email}</Text>
                  <Text style={{ color: colors.primary, fontSize: 12, fontFamily: fonts.semiBold, marginTop: 2 }}>
                    {item.role}
                    {item.identityVerified ? ' · Đã xác minh CCCD' : ''}
                  </Text>
                </View>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                  <Text style={{ fontFamily: fonts.bold, color: colors.text }}>{item.coinBalance}</Text>
                  <CoinIcon size={14} />
                </View>
              </View>
            </Card>
          </Pressable>
        )}
      />
      <View style={{ flexDirection: 'row', gap: 8 }}>
        <Button title="Trang trước" variant="outline" disabled={page <= 1} onPress={() => setPage((p) => p - 1)} />
        <Button title="Trang sau" variant="outline" disabled={!hasNextPage} onPress={() => setPage((p) => p + 1)} />
      </View>
    </ScreenContainer>
  );
}
